import type { JobsListQueryParams } from '@/api/jobs.api'
import type { AssessmentTemplatesListQueryParams } from '@/api/assessment-templates.api'
import type { ShortlistRule } from '@/types/shortlist-rule.type'

export const CACHE_KEYS = {
    ADMIN_COMPANIES: 'admin-companies',
    ADMIN_SIGNUP_DRAFTS: 'admin-signup-drafts',
    JOBS: 'jobs',
    JOBS_LIST: 'jobs-list',
    JOBS_DETAIL: 'jobs-detail',
    CANDIDATE_NOTES: 'candidate-notes',
    JOB_APPLICATIONS: 'jobApplications',
    ASSESSMENT_TEMPLATES: 'assessment-templates',
    ASSESSMENT_TEMPLATES_LIST: 'assessment-templates-list',
    ASSESSMENT_TEMPLATES_DETAIL: 'assessment-templates-detail',
} as const

const stableStringify = (value: unknown) => JSON.stringify(value ?? {})

export const jobsQueryKeys = {
    all: [CACHE_KEYS.JOBS] as const,
    list: (params: JobsListQueryParams) => [CACHE_KEYS.JOBS, CACHE_KEYS.JOBS_LIST, stableStringify(params)] as const,
    detail: (id: number, companySlug: string) => [CACHE_KEYS.JOBS, CACHE_KEYS.JOBS_DETAIL, id, companySlug] as const,
}

export const adminCompaniesQueryKeys = {
    all: [CACHE_KEYS.ADMIN_COMPANIES] as const,
    list: () => [CACHE_KEYS.ADMIN_COMPANIES, 'list'] as const,
}

export const adminSignupDraftsQueryKeys = {
    all: [CACHE_KEYS.ADMIN_SIGNUP_DRAFTS] as const,
    list: () => [CACHE_KEYS.ADMIN_SIGNUP_DRAFTS, 'list'] as const,
}

export const candidateNotesQueryKeys = {
    all: [CACHE_KEYS.CANDIDATE_NOTES] as const,
    byEmail: (email: string, companyId: number) => [CACHE_KEYS.CANDIDATE_NOTES, 'by-email', email, companyId] as const,
}

export const jobApplicationsQueryKeys = {
    all: [CACHE_KEYS.JOB_APPLICATIONS] as const,
    list: (
        jobPostingId: number,
        pageSize: number,
        filterModel: unknown,
        sortModel: unknown,
        shortlistMode: boolean,
        excludeShortlistedMode: boolean,
        shortlistRule: ShortlistRule | null
    ) =>
        [
            ...jobApplicationsQueryKeys.all,
            'list',
            jobPostingId,
            pageSize,
            stableStringify(filterModel),
            stableStringify(sortModel),
            shortlistMode,
            excludeShortlistedMode,
            stableStringify(shortlistRule),
        ] as const,
    detail: (jobApplicationId: number) => [...jobApplicationsQueryKeys.all, 'detail', jobApplicationId] as const,
    locations: (jobPostingId: number) => [...jobApplicationsQueryKeys.all, 'locations', jobPostingId] as const,
}

export const assessmentTemplatesQueryKeys = {
    all: [CACHE_KEYS.ASSESSMENT_TEMPLATES] as const,
    list: (params: AssessmentTemplatesListQueryParams) =>
        [CACHE_KEYS.ASSESSMENT_TEMPLATES, CACHE_KEYS.ASSESSMENT_TEMPLATES_LIST, stableStringify(params)] as const,
    detail: (id: number | string, companySlug: string) =>
        [CACHE_KEYS.ASSESSMENT_TEMPLATES, CACHE_KEYS.ASSESSMENT_TEMPLATES_DETAIL, String(id), companySlug] as const,
}
